import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/Button';
import { InputSearch } from '@/components/Input/InputSearch';
import Pagination from '@/components/Pagination/Pagination';
import { TableData } from '@/components/Table/TableData';
import { showErrorToast } from '@/components/toast/globalToast';
import {
  listSandboxOrders,
  SandboxOrder,
  SandboxOrderStatus,
} from '@/services/api/sandbox';
import { Column } from '@/types/common';
import { extractApiErrorMessage } from '@/utils/errorHandler';
import {
  ORDER_STATUSES,
  OrderStatusBadge,
  orderStatusLabel,
  totalQty,
} from './sandboxOrderUi';
import { formatTime } from './sandboxUi';

/**
 * The client's sandbox outbound orders, newest first — the same table card
 * as the sandbox shipments (search, status filter, paging). A row opens its
 * detail.
 */
const SandboxOrderList: React.FC<{
  onCreate: () => void;
  onOpen: (id: string) => void;
  /** Bumped by the parent to reload (e.g. after a create). */
  reloadKey: number;
}> = ({ onCreate, onOpen, reloadKey }) => {
  const [rows, setRows] = useState<SandboxOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<SandboxOrderStatus | ''>('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listSandboxOrders({
        page,
        limit,
        status: status || undefined,
        search: search || undefined,
      });
      setRows(res.rows);
      setTotal(res.total);
    } catch (err) {
      showErrorToast(
        'Failed to load sandbox orders',
        extractApiErrorMessage(err, 'Failed to load sandbox orders')
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const columns: Column<SandboxOrder>[] = [
    {
      key: 'code',
      title: 'Order',
      render: (_v, r) => (
        <span className="font-mono text-xs font-semibold text-primary">
          {r.code}
        </span>
      ),
    },
    {
      key: 'externalID',
      title: 'External ID',
      render: (_v, r) => (
        <span className="font-mono text-xs text-text-sub-600">
          {r.externalID}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (_v, r) => <OrderStatusBadge status={r.status} />,
    },
    {
      key: 'lines',
      title: 'Lines',
      render: (_v, r) => (
        <span className="text-xs tabular-nums text-text-strong-950">
          {r.lines.length} SKU · {totalQty(r.lines)} qty
        </span>
      ),
    },
    {
      key: 'recipient',
      title: 'Recipient',
      render: (_v, r) => (
        <span className="text-xs text-text-sub-600">{r.recipient.name}</span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created',
      render: (_v, r) => (
        <span className="text-xs tabular-nums text-text-sub-600">
          {formatTime(r.createdAt)}
        </span>
      ),
    },
  ];

  const filtered = Boolean(status || search);

  return (
    <div className="rounded-xl border border-stroke-soft-200 bg-bg-white-0 shadow-card-xs">
      <div className="flex flex-wrap items-center gap-3 border-b border-stroke-soft-200 p-4">
        <InputSearch
          className="w-full sm:min-w-[200px] sm:max-w-[360px] sm:flex-1"
          placeholder="Search order code or external ID…"
          onSearch={(v) => {
            setPage(1);
            setSearch(v.trim());
          }}
          onClear={() => {
            setPage(1);
            setSearch('');
          }}
        />
        <label className="sr-only" htmlFor="sbx-order-status">
          Status
        </label>
        <select
          id="sbx-order-status"
          value={status}
          onChange={(e) => {
            setPage(1);
            setStatus(e.target.value as SandboxOrderStatus | '');
          }}
          className="h-9 rounded-lg border border-stroke-soft-200 bg-bg-white-0 px-3 text-sm text-text-strong-950 focus:border-primary focus:outline-none"
        >
          <option value="">All statuses</option>
          {ORDER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {orderStatusLabel(s)}
            </option>
          ))}
        </select>
        <div className="ml-auto">
          <Button onClick={onCreate}>
            <i className="ri-add-line mr-1.5" />
            New sandbox order
          </Button>
        </div>
      </div>

      <TableData<SandboxOrder>
        data={rows}
        columns={columns}
        loading={loading}
        className="rounded-none border-0 shadow-none"
        onRowClick={(r) => onOpen(r.id)}
        renderMobileCard={(r) => (
          <div className="flex items-start justify-between gap-3 p-4">
            <div className="min-w-0">
              <p className="truncate font-mono text-xs font-semibold text-primary">
                {r.code}
              </p>
              <p className="truncate font-mono text-xs text-text-sub-600">
                {r.externalID}
              </p>
              <p className="mt-1 text-xs text-text-soft-400">
                {formatTime(r.createdAt)}
              </p>
            </div>
            <OrderStatusBadge status={r.status} />
          </div>
        )}
        emptyState={
          <div className="px-6 py-12 text-center">
            <i className="ri-archive-stack-line text-2xl text-text-soft-400" />
            <p className="mt-2 text-sm font-medium text-text-strong-950">
              {filtered ? 'No sandbox orders match' : 'No sandbox orders yet'}
            </p>
            <p className="mt-1 text-xs text-text-sub-600">
              {filtered
                ? 'Clear the search or the status filter.'
                : 'Create one with SKUs from your catalog to see the order Dash returns.'}
            </p>
          </div>
        }
      />

      {total > 0 && (
        <div className="border-t border-stroke-soft-200 px-4 pb-4">
          <Pagination
            currentPage={page}
            totalItems={total}
            itemsPerPage={limit}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPage(1);
              setLimit(size);
            }}
            pageSizeOptions={[10, 20, 50]}
          />
        </div>
      )}
    </div>
  );
};

export default SandboxOrderList;
